import { z } from "zod";

import {
  ELEMENT_KINDS,
  ICONS,
  LAYOUTS,
  MAX_HEADER_ACTIONS,
  WIDGET_KINDS,
  type CompiledManifest,
  type ViewControl,
  type LayoutKind,
  type SlotKind,
  type ViewManifest,
  type WidgetKind,
} from "../api/generated/contracts";

const recordSchema = z.record(z.string(), z.unknown());
const fieldSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    type: z.enum([
      "text",
      "number",
      "boolean",
      "date",
      "time",
      "datetime",
      "select",
      "multi_select",
      "reference",
    ]),
    required: z.boolean(),
    control: z.string().min(1),
    default: z.unknown().optional(),
    config: recordSchema.optional(),
  })
  .loose();
const collectionSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    primary_display_field_id: z.string().min(1),
    fields: z.array(fieldSchema),
  })
  .loose();
const elementSchema = z
  .object({
    id: z.string().min(1),
    kind: z.string().min(1),
    span: z.union([z.literal(1), z.literal(2)]).optional(),
    config: recordSchema.optional(),
  })
  .loose();
const commandSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("create_record"),
      collection_id: z.string().min(1),
      defaults: recordSchema.optional(),
    })
    .loose(),
  z
    .object({ kind: z.literal("open_view"), view_id: z.string().min(1) })
    .loose(),
  z
    .object({ kind: z.literal("print"), print_form_id: z.string().min(1) })
    .loose(),
]);
const controlSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    icon: z.enum(ICONS as [string, ...string[]]).optional(),
    command: commandSchema,
  })
  .loose();
const sectionSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().optional(),
    description: z.string().optional(),
    columns: z.union([z.literal(1), z.literal(2)]),
    slot: z.string().optional(),
    fab: controlSchema.optional(),
    elements: z.array(elementSchema),
  })
  .loose();
const viewSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    role: z.enum(["home", "standard"]),
    show_in_navigation: z.boolean(),
    context: z
      .object({
        kind: z.enum(["none", "record"]),
        collection_id: z.string().optional(),
      })
      .loose(),
    layout: z.string().optional(),
    header_actions: z.array(controlSchema).max(MAX_HEADER_ACTIONS).optional(),
    fab: controlSchema.optional(),
    sections: z.array(sectionSchema),
  })
  .loose();
const manifestSchema = z
  .object({
    manifest_version: z.literal("1.0"),
    schema_format_version: z.literal("1.0"),
    compiler_version: z.string(),
    registry_version: z.string(),
    checksum: z.string(),
    collections: z.array(collectionSchema),
    views: z.array(viewSchema),
    actions: z.array(recordSchema),
    print_forms: z.array(recordSchema),
  })
  .loose();

interface ManifestDiagnostic {
  code: string;
  path: string;
  message: string;
}

/** Same rules and codes as the backend views compiler pass (ADR 0185). */
function layoutDiagnostics(view: ViewManifest): ManifestDiagnostic[] {
  // Typed as string: an unvalidated manifest can carry any name here.
  const layoutId: string = view.layout ?? "stack";
  if (!Object.hasOwn(LAYOUTS, layoutId))
    return [
      {
        code: "unsupported_layout",
        path: `/views/${view.id}/layout`,
        message: "unsupported layout",
      },
    ];
  const layout = LAYOUTS[layoutId as LayoutKind];
  const diagnostics: ManifestDiagnostic[] = [];
  const counts = new Map<string, number>();
  for (const section of view.sections) {
    const slotId: string = section.slot ?? layout.defaultSlot;
    const rule = layout.slots[slotId as SlotKind];
    const path = `/views/${view.id}/sections/${section.id}`;
    if (!rule) {
      diagnostics.push({
        code: "slot_not_in_layout",
        path: `${path}/slot`,
        message: "slot does not exist in the view layout",
      });
      continue;
    }
    counts.set(slotId, (counts.get(slotId) ?? 0) + 1);
    if (rule.titleRequired && !section.title)
      diagnostics.push({
        code: "tab_title_required",
        path: `${path}/title`,
        message: "a section in this slot needs a title",
      });
    if (
      rule.maxElements !== undefined &&
      section.elements.length > rule.maxElements
    )
      diagnostics.push({
        code: "summary_element_limit",
        path: `${path}/elements`,
        message: "the slot holds too many elements",
      });
  }
  for (const [slotId, rule] of Object.entries(layout.slots)) {
    const count = counts.get(slotId) ?? 0;
    if (
      count < rule.minSections ||
      (rule.maxSections !== undefined && count > rule.maxSections)
    )
      diagnostics.push({
        code: "slot_section_limit",
        path: `/views/${view.id}/layout`,
        message: "the slot holds a number of sections outside its bounds",
      });
  }
  return diagnostics;
}

/** A well-formed command of a control or button, else undefined. */
export function parseControlCommand(
  value: unknown,
): ViewControl["command"] | undefined {
  const parsed = commandSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Command targets must exist, control ids are unique per view and a section
 * fab needs the tabs layout. Paths and codes mirror the backend pass.
 */
function controlDiagnostics(
  manifest: CompiledManifest,
  view: ViewManifest,
): ManifestDiagnostic[] {
  const diagnostics: ManifestDiagnostic[] = [];
  const seen = new Set<string>();
  const checkCommand = (
    command: ViewControl["command"],
    path: string,
  ): void => {
    if (
      command.kind === "create_record" &&
      !manifest.collections.some((item) => item.id === command.collection_id)
    )
      diagnostics.push({
        code: "unknown_command_collection",
        path: `${path}/collection_id`,
        message: "command targets an unknown collection",
      });
    if (
      command.kind === "open_view" &&
      !manifest.views.some((item) => item.id === command.view_id)
    )
      diagnostics.push({
        code: "unknown_command_view",
        path: `${path}/view_id`,
        message: "command targets an unknown view",
      });
    if (
      command.kind === "print" &&
      !manifest.print_forms.some((form) => form.id === command.print_form_id)
    )
      diagnostics.push({
        code: "unknown_command_print_form",
        path: `${path}/print_form_id`,
        message: "command targets an unknown print form",
      });
  };
  const checkControl = (control: ViewControl | undefined, path: string) => {
    if (!control) return;
    if (seen.has(control.id))
      diagnostics.push({
        code: "duplicate_control_id",
        path,
        message: "control id is already used in this view",
      });
    seen.add(control.id);
    checkCommand(control.command, `${path}/command`);
  };
  const base = `/views/${view.id}`;
  for (const control of view.header_actions ?? [])
    checkControl(control, `${base}/header_actions/${control.id}`);
  checkControl(view.fab, `${base}/fab`);
  for (const section of view.sections)
    checkControl(section.fab, `${base}/sections/${section.id}/fab`);
  for (const section of view.sections) {
    const path = `${base}/sections/${section.id}`;
    if (section.fab && (view.layout ?? "stack") !== "tabs")
      diagnostics.push({
        code: "section_fab_outside_tabs",
        path: `${path}/fab`,
        message: "a section fab is only valid in the tabs layout",
      });
    for (const element of section.elements) {
      if (element.kind !== "button" || !element.config?.command) continue;
      const command = parseControlCommand(element.config.command);
      if (command)
        checkCommand(command, `${path}/elements/${element.id}/config/command`);
    }
  }
  return diagnostics;
}

export function validateManifest(
  value: unknown,
):
  | { ok: true; manifest: CompiledManifest }
  | { ok: false; diagnostics: ManifestDiagnostic[] } {
  const parsed = manifestSchema.safeParse(value);
  if (!parsed.success)
    return {
      ok: false,
      diagnostics: parsed.error.issues.map((issue) => ({
        code: "invalid_manifest",
        path: issue.path.length ? `/${issue.path.map(String).join("/")}` : "/",
        message: issue.message,
      })),
    };
  const manifest = parsed.data as unknown as CompiledManifest;
  const diagnostics: ManifestDiagnostic[] = [];
  if (manifest.views.filter((view) => view.role === "home").length !== 1)
    diagnostics.push({
      code: "home_view_count",
      path: "/views",
      message: "manifest must have one home view",
    });
  for (const view of manifest.views)
    diagnostics.push(
      ...layoutDiagnostics(view),
      ...controlDiagnostics(manifest, view),
    );
  for (const view of manifest.views)
    for (const section of view.sections)
      for (const element of section.elements) {
        if (!ELEMENT_KINDS.includes(element.kind))
          diagnostics.push({
            code: "unsupported_element",
            path: `/views/${view.id}/sections/${section.id}/elements/${element.id}`,
            message: "unsupported renderer element",
          });
        if (
          element.kind === "widget" &&
          !WIDGET_KINDS.includes(String(element.config?.widget) as WidgetKind)
        )
          diagnostics.push({
            code: "unsupported_widget",
            path: `/views/${view.id}/sections/${section.id}/elements/${element.id}`,
            message: "unsupported widget",
          });
      }
  return diagnostics.length
    ? { ok: false, diagnostics }
    : { ok: true, manifest };
}
